import React, { useState, useEffect } from "react";
import { useClientsReviewsData } from "@/contexts/HomeDataContext";
import { FadeIn, SlideLeft, SlideRight } from "@/components/ui/scroll-animated";
import { useTranslations } from 'next-intl';

export const ClientsReviewsSection: React.FC = () => {
  const data = useClientsReviewsData();
  const t = useTranslations('Reviews');
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  
  // Get the number of clients from the translation file
  const getClientCount = () => {
    try {
      // Try to get the length of the clients array
      const clients = t.raw('clients');
      return Array.isArray(clients) ? clients.length : 1;
    } catch (e) {
      // Fallback to 1 if there's an error
      return 1;
    }
  };
  
  const clientCount = getClientCount();

  // Auto-slide functionality with progress animation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReviewIndex((prevIndex) => 
        (prevIndex + 1) % clientCount
      );
      setProgress(0); // Reset progress when changing review
    }, 5000); // Change review every 5 seconds

    return () => clearInterval(interval);
  }, [clientCount]);

  // Progress animation
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress + (100 / (5000 / 50)); // 50ms intervals
        return newProgress >= 100 ? 0 : newProgress;
      });
    }, 50); // Update every 50ms for smooth animation

    return () => clearInterval(progressInterval);
  }, [currentReviewIndex]);

  const currentReview = data.reviews[currentReviewIndex];

  const renderStars = (rating: number | string) => {
    const numRating = typeof rating === 'string' ? parseFloat(rating) : rating;
    const stars = [];
    const fullStars = Math.floor(numRating);
    const hasHalfStar = numRating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative w-4 h-4">
          {/* Gray background star */}
          <svg className="absolute w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          {/* Yellow half star - RTL support */}
          <div className="absolute top-0 left-0 rtl:right-0 rtl:left-auto w-1/2 h-full overflow-hidden">
            <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        </div>
      );
    }

    const emptyStars = 5 - Math.ceil(numRating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <svg key={`empty-${i}`} className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }

    return stars;
  };

  return (
    <section className="relative w-full py-16 md:py-20 lg:py-24 bg-[#05092B] overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[520px] md:min-h-[560px] lg:min-h-[620px]">
          
          {/* Left Section - SVG Background */}
          <div className="hidden lg:block absolute bottom-0 left-0 pointer-events-none select-none">
            <SlideLeft delay={200} className="relative w-full lg:h-[600px]">
              <div className="relative w-full h-full flex items-center justify-center">
                <img
                  src="/svg/clients-reviews/left_section.svg"
                  alt="Clients Reviews Visual Elements"
                  className="w-full h-full object-contain object-center"
                />
              </div>
            </SlideLeft>
          </div>

          {/* Right Section - Reviews Content */}
          <div className="relative order-1 lg:order-2 rtl:lg:order-1 lg:col-start-2 lg:col-span-1 lg:ml-auto max-w-2xl mx-auto lg:mx-0 w-full">
            <SlideRight delay={400} className="space-y-8">
            {/* Header */}
            <div className="space-y-4">
              <FadeIn delay={600}>
                <div className="bg-brand-gradient-text font-h1-up font-[number:var(--h1-up-font-weight)] text-[length:var(--h1-up-font-size)] tracking-[var(--h1-up-letter-spacing)] leading-[var(--h1-up-line-height)] [font-style:var(--h1-up-font-style)]">
                  {t('tagline')}
                </div>
              </FadeIn>
              
              <FadeIn delay={700}>
                <h2 className="font-heading-h1 font-[number:var(--heading-h1-font-weight)] text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[length:var(--heading-h1-font-size)] tracking-[var(--heading-h1-letter-spacing)] leading-tight md:leading-tight lg:leading-[var(--heading-h1-line-height)] [font-style:var(--heading-h1-font-style)]">
                  {t('heading')}
                </h2>
              </FadeIn>
            </div>

            {/* Review Card */}
            <FadeIn delay={800}>
              <div className="bg-[#1A203B] rounded-lg p-5 md:p-6 lg:p-8 shadow-xl">
                {/* Client Info */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-heading-h4 font-[number:var(--heading-h4-font-weight)] text-white text-[length:var(--heading-h4-font-size)] tracking-[var(--heading-h4-letter-spacing)] leading-[var(--heading-h4-line-height)] [font-style:var(--heading-h4-font-style)]">
                      {t(`clients.${currentReviewIndex}.name`)}
                    </h3>
                    <p className="text-gray-400 font-body-m font-[number:var(--body-m-font-weight)] text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)]">
                      {t(`clients.${currentReviewIndex}.title`)}
                    </p>
                  </div>
                  
                  {/* Star Rating */}
                  <div className="flex items-center space-x-1.5">
                    {renderStars(t(`clients.${currentReviewIndex}.rating`))}
                  </div>
                </div>

                {/* Review Text */}
                <p className="text-gray-300 font-body-m font-[number:var(--body-m-font-weight)] text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-relaxed md:leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)]">
                  {t(`clients.${currentReviewIndex}.review`)}
                </p>
              </div>
            </FadeIn>

            {/* Progress Indicator */}
            <FadeIn delay={900}>
              <div className="flex justify-center">
                <div className="flex space-x-2 rtl:space-x-reverse">
                  {Array.from({ length: clientCount }, (_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentReviewIndex(index);
                        setProgress(0); // Reset progress when manually clicking
                      }}
                      className={`h-1.5 md:h-1 rounded-full transition-all duration-300 ${
                        index === currentReviewIndex 
                          ? 'w-8' 
                          : 'w-2'
                      }`}
                    >
                      {/* Active state with progress */}
                      {index === currentReviewIndex ? (
                        <div className="relative w-full h-full bg-gray-600 rounded-full overflow-hidden">
                          <div 
                            className="absolute top-0 left-0 rtl:right-0 rtl:left-auto h-full bg-brand-gradient rounded-full transition-all duration-75 ease-linear"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      ) : (
                        /* Inactive state */
                        <div className="w-full h-full bg-gray-600 hover:bg-gray-500 rounded-full transition-colors duration-300" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </FadeIn>
            </SlideRight>
          </div>
        </div>
      </div>
    </section>
  );
};